import { parseARS } from '@gl/shared';
import { useEffect, useState, type ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

function centsToText(cents: number | null): string {
  if (cents === null) return '';
  const pesos = cents / 100;
  return Number.isInteger(pesos) ? String(pesos) : pesos.toFixed(2).replace('.', ',');
}

/** Input de pesos ("1.234,50") que expone centavos. `null` = vacío o inválido. */
export function MoneyInput({
  value,
  onChange,
  className,
  ...props
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> & {
  value: number | null;
  onChange: (cents: number | null) => void;
}) {
  const [text, setText] = useState(() => centsToText(value));

  useEffect(() => {
    setText((prev) => (parseARS(prev) === value ? prev : centsToText(value)));
  }, [value]);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground">$</span>
      <Input
        {...props}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        className={cn('pl-7 tabular-nums', className)}
        value={text}
        onChange={(e) => {
          const next = e.target.value.replace(/[^\d.,]/g, '');
          setText(next);
          const cents = parseARS(next);
          onChange(cents !== null && cents >= 0 ? cents : null);
        }}
      />
    </div>
  );
}
